import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Tabs, Descriptions, Avatar, Badge, Button, Space, Card, Typography, Tag, Form, Input, Select, message } from "antd";
import * as Icon from "react-feather";
import TableComponent from "../../components/widgets/table/index";
import { useQuery, useMutation, useQueryClient } from "react-query"; // Add useMutation and useQueryClient
import { api_srv } from "services";
import { complete_client_direct_quoation, request_payment_direct_quoation, sendInvoice, sendQuotationToClient} from "services/apiSrv";
import AddPayment from "components/widgets/modals/AddPayment";
import { ModalContext } from "context/ModalContext";
import { show_toast } from "utils/helpers";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

export default function ViewDirectQuotation() {
  const { id } = useParams();
  const [quotationData, setQuotationData] = useState(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingBenefits, setIsEditingBenefits] = useState(false);
  const [isEditingRisk, setIsEditingRisk] = useState(false);
  const [clientForm] = Form.useForm();
  const [benefitsForm] = Form.useForm();
  const [riskForm] = Form.useForm();
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);
  const queryClient = useQueryClient(); // Initialize queryClient for cache management

  // Fetch quotation details
  const get_quotation_details = async () => {
    let resp = await (await api_srv).fetchClientQuotationDetails(id);
    return resp;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['quotation_details'],
    queryFn: get_quotation_details,
    onSuccess: (data) => {
      if (data?.data?.length > 0) {
        setQuotationData(data.data[0]);
      }
    },
  });

  // Mutation for completing the quotation
  const completeMutation = useMutation({
    mutationFn: (systemQuotationRef) => complete_client_direct_quoation(systemQuotationRef),
    onSuccess: () => {
      // Update local state
      setQuotationData((prev) => ({ ...prev, quotationStatus: 'APPROVED' }));
      // Invalidate query to refresh data if needed
      queryClient.invalidateQueries(['quotation_details']);
      message.success('Quotation completed successfully');
    },
    onError: (error) => {
      message.error(`Failed to complete: ${error.message || 'Unknown error'}`);
    },
  });

  // Handle complete action
  const handleComplete = () => {
    if (quotationData?.systemQuotationRef) {
      completeMutation.mutate(quotationData.systemQuotationRef);
    } else {
      message.error('Quotation reference not found');
    }
  };

  // Calculate cover end date
  const calculateCoverEndDate = (startDate, coverPeriod) => {
    const start = new Date(startDate);
    start.setDate(start.getDate() + (coverPeriod || 0));
    return start.toLocaleDateString();
  };

  // Initialize form values with optional chaining
  useEffect(() => {
    if (quotationData) {
      clientForm.setFieldsValue({
        firstName: quotationData?.clientDetails?.names?.firstName,
        lastName: quotationData?.clientDetails?.names?.lastName,
        email: quotationData?.clientDetails?.email,
        kraPin: quotationData?.clientDetails?.kraPin,
        kycType: quotationData?.clientDetails?.kyc?.[0] === "I" ? "INDIVIDUAL" : "CORPORATE",
      });
      benefitsForm.setFieldsValue({
        benefits: quotationData?.quotationAdditionalBenefits || [],
      });
      riskForm.setFieldsValue({
        value: quotationData?.risks?.[0]?.value,
        RiskType: quotationData?.risks?.[0]?.riskType,
        model: quotationData?.risks?.[0]?.details?.model,
        year: quotationData?.risks?.[0]?.details?.year,
        color: quotationData?.risks?.[0]?.details?.color,
        chassis: quotationData?.risks?.[0]?.chassisNumber,
      });
    }
  }, [quotationData, clientForm, benefitsForm, riskForm]);

  if (isLoading) return <div>Loading...</div>;
  if (error || !quotationData) return <div>Error: {error?.message || "No quotation data found"}</div>;
  console.log("quotationdata===", quotationData);

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      pending: '#faad14',
      active: '#52c41a',
      approved: '#1890ff',
      rejected: '#ff4d4f',
      cancelled: '#ff4d4f',
      draft: '#722ed1',
    };
    return statusColors[status?.toLowerCase()] || '#52c41a';
  };

  // Handle form submissions
  const handleClientUpdate = (values) => {
    setQuotationData((prev) => ({
      ...prev,
      clientDetails: {
        ...prev?.clientDetails,
        names: {
          fname: values.names.firstName,
          lname: values.names.lastName,
        },
        email: values.email,
        kraPin: values.kraPin,
        kyc: values.kycType === "INDIVIDUAL" ? { 0: "I" } : { 0: "C" },
      },
    }));
    setIsEditingClient(false);
    message.success('Client details updated successfully');
  };

  const handleBenefitsUpdate = (values) => {
    setQuotationData((prev) => ({
      ...prev,
      quotationAdditionalBenefits: values.benefits || [],
    }));
    setIsEditingBenefits(false);
    message.success('Benefits updated successfully');
  };

  const handleRiskUpdate = (values) => {
    setQuotationData((prev) => ({
      ...prev,
      risks: [
        {
          ...prev?.risks?.[0],
          value: values.value,
          details: {
            ...prev?.risks?.[0]?.details,
            make: values.make,
            model: values.model,
            year: values.year,
            color: values.color,
            chassis: values.chassis,
          },
        },
      ],
    }));
    setIsEditingRisk(false);
    message.success('Risk details updated successfully');
  };

  const handleRequestPayment = () => {
    setModalShow(true);
  };

  const handleSendInvoice = async () => {
    try {
      const res = await sendInvoice(quotationData.quotationPayment.invoiceSystemRef);
      show_toast('Invoice sent successfully', 'success');
      message.success('Invoice sent successfully');
    } catch (error) {
      show_toast('Failed to send invoice!', 'error');
    }
  };

  const handleSendQuote = async () => {
    await sendQuotationToClient(id)
    console.log("Sending quotation for quotation:", id);
    message.success('Quotation sent successfully');
  };

  const handleCancel = () => {
    setQuotationData((prev) => ({ ...prev, quotationStatus: 'CANCELLED' }));
    message.success('Quotation cancelled');
  };

  const handleViewInvoice = () => {
    console.log("Viewing invoice for quotation:", id);
  };

  // Table configuration for benefits
  const benefitsTableFields = [
    { name: "Name" },
    { name: "Description" },
    { name: "Status" },
    { name: "Created Date" },
    ...(isEditingBenefits ? [{ name: "Actions" }] : []),
  ];

  const benefitsTableRowData = [
    { type: "text", field: "benefit_category_name" },
     { type: "text", field: "benefit_category_description" },
    { type: "text", field: "status" },
    { type: "date", field: "created_date" },
    ...(isEditingBenefits
      ? [
          {
            type: "action",
            field: "actions",
            render: (record) => (
              <Space>
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    const newBenefits =
                      quotationData?.product_details?.productBenefits?.filter((b) => b.id !== record.id) || [];
                    handleBenefitsUpdate({ benefits: newBenefits });
                  }}
                >
                  Delete
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
      <AddPayment show={modalShow} onHide={modalClose} systemQuotationRef={quotationData?.systemQuotationRef} />
      {/* Header Section */}
      <Card
        style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>
                Direct Quotation
              </Text>
              <Title level={3} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>
                {quotationData?.quotationInternalRef || 'N/A'}
              </Title>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Badge
                color={getStatusColor(quotationData?.quotationStatus)}
                text={quotationData?.quotationStatus?.toUpperCase() || 'UNKNOWN'}
                style={{ fontSize: '12px', fontWeight: '600' }}
              />
              <Space>
                {(quotationData?.quotationStatus === 'active') &&  <Button
                  type="primary"
                  icon={<Icon.Check size={16} />}
                  onClick={handleComplete}
                  loading={completeMutation.isLoading} // Show loading state
                >
                  Complete
                </Button>}
                 {(quotationData?.quotationPayment?.paymentStatus === 'pending') &&
                  (<Button
                  type="default"
                  onClick={handleRequestPayment}
                >
                  Request Payment
                </Button>)}

                
                 {(quotationData?.quotationStatus === 'completed') && (<Button
                  type="default"
                  icon={<Icon.Mail size={16} />}
                  onClick={handleSendInvoice}
                >
                  Send Invoice
                </Button>)}
               {(quotationData?.quotationStatus === 'completed') && ( <Button
                  type="default"
                  onClick={handleSendQuote}
                >
                  Send Quotation
                </Button>)}
                {/* <Button
                  type="default"
                  icon={<Icon.X size={16} />}
                  onClick={handleCancel}
                  // disabled={quotationData?.quotationStatus === 'CANCELLED'}
                     disabled={
                    
                      quotationData?.quotationStatus === 'active'
                    }
                >
                  Cancel
                </Button> */}
              </Space>
            </div>
          </div>
        }
      >
        {/* Rest of the Card content remains unchanged */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
              Product Details
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Product Name: {quotationData?.product_details?.productName || 'N/A'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Cover Type: {quotationData?.quotationCoverDetails?.coverType || 'N/A'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Underwriter: {quotationData?.underwriter_details?.name || 'N/A'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Cover Period: {quotationData?.quotationCoverDetails?.coverPeriod || 0} days
            </div>
          </div>
          <div style={{ textAlign: 'right', flex: 1 }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Created: {quotationData?.createdAt ? new Date(quotationData.createdAt).toLocaleDateString() : 'N/A'}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>
              {quotationData?.quotationCurrency || ''} {quotationData?.quotationPremium?.totalPremium?.toLocaleString() || '0'}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Total Premium
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs and other components remain unchanged */}
      <Tabs defaultActiveKey="1" size="large" style={{ marginBottom: '24px' }}>
        <TabPane tab={<Space><Icon.FileText size={16} /> Product Details</Space>} key="1">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
            <Descriptions
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon.Shield size={18} style={{ color: '#1890ff' }} />
                  <span style={{ fontWeight: 600 }}>Coverage Information</span>
                </div>
              }
              bordered
              column={2}
              size="small"
              style={{ background: '#fff', borderRadius: '8px' }}
            >
              <Descriptions.Item label="Insurance Type">
                <Badge color="blue" text={quotationData?.product_details?.insuranceCategory || 'N/A'} />
              </Descriptions.Item>
              <Descriptions.Item label="Cover Type">
                <Badge color="green" text={quotationData?.quotationCoverDetails?.coverType || 'N/A'} />
              </Descriptions.Item>
              <Descriptions.Item label="Product">
                <Tag color="purple">{quotationData?.product_details?.productName || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Underwriter">
                <Tag color="cyan">{quotationData?.underwriter_details?.name || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cover Period">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icon.Clock size={14} />
                  <span>{quotationData?.quotationCoverDetails?.coverPeriod || 0} days</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                <Text style={{ lineHeight: '1.6' }}>{quotationData?.quotationDescription || 'No description available'}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 600 }}>Premium Information</span>
              </div>
              <Space>
                <Button
                  type="default"
                  icon={<Icon.FileText size={16} />}
                  onClick={handleViewInvoice}
                >
                  View Invoice
                </Button>
              </Space>
            </div>
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ background: '#fff', borderRadius: '8px' }}
            >
              <Descriptions.Item label="Total Premium">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{quotationData?.quotationCurrency || ''} {quotationData?.quotationPremium?.totalPremium?.toLocaleString() || '0'}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Base Premium">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{quotationData?.quotationCurrency || ''} {quotationData?.quotationPremium?.basePremium?.toLocaleString() || '0'}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Charges">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{quotationData?.quotationCurrency || ''} {quotationData?.quotationPremium?.totalCharges?.toLocaleString() || '0'}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Additional Premium">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{quotationData?.quotationCurrency || ''} {quotationData?.quotationPremium?.additionalPremium?.toLocaleString() || '0'}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge color={getStatusColor(quotationData?.quotationStatus)} text={quotationData?.quotationStatus?.toUpperCase() || 'UNKNOWN'} />
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane tab={<Space><Icon.CheckCircle size={16} /> Benefits ({quotationData?.quotationAdditionalBenefits?.length || 0})</Space>} key="2">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Included Benefits</Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Coverage benefits for this quotation
                </Text>
              </div>
              <Space>
                {isEditingBenefits ? (
                  <Space>
                    <Button onClick={() => setIsEditingBenefits(false)}>Cancel</Button>
                    <Button type="primary" onClick={() => benefitsForm.submit()}>
                      Save Benefits
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    icon={<Icon.Edit size={16} />}
                    onClick={() => setIsEditingBenefits(true)}
                  >
                    Edit Benefits
                  </Button>
                )}
                <Badge.Ribbon text={`${quotationData?.product_details?.productBenefits?.length || 0} Benefits`} color="green">
                  <div style={{ width: '100%', height: '32px' }} />
                </Badge.Ribbon>
              </Space>
            </div>
            {isEditingBenefits ? (
              <Form
                form={benefitsForm}
                onFinish={handleBenefitsUpdate}
                initialValues={{ benefits: quotationData?.product_details?.productBenefits || [] }}
              >
                <Form.List name="benefits">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} style={{ marginBottom: '16px', borderRadius: '8px' }}>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Form.Item
                              {...restField}
                              name={[name, 'name']}
                              rules={[{ required: true, message: 'Please input benefit name' }]}
                              label="Benefit Name"
                            >
                              <Input placeholder="Benefit Name" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'description']}
                              rules={[{ required: true, message: 'Please input description' }]}
                              label="Description"
                            >
                              <Input.TextArea placeholder="Description" rows={3} />
                            </Form.Item>
                            <Space>
                              <Form.Item
                                {...restField}
                                name={[name, 'type']}
                                rules={[{ required: true, message: 'Please select type' }]}
                                label="Type"
                              >
                                <Select placeholder="Select Type" style={{ width: 120 }}>
                                  <Option value="HEALTH">HEALTH</Option>
                                  <Option value="MOTOR">MOTOR</Option>
                                </Select>
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'limit']}
                                rules={[{ required: true, message: 'Please input limit' }]}
                                label="Limit"
                              >
                                <Input type="number" placeholder="Limit" min={0} style={{ width: 120 }} />
                              </Form.Item>
                            </Space>
                            <Button
                              danger
                              onClick={() => remove(name)}
                              icon={<Icon.Trash size={16} />}
                            >
                              Remove Benefit
                            </Button>
                          </Space>
                        </Card>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            id: `${Date.now()}`,
                            name: '',
                            description: '',
                            type: 'HEALTH',
                            limit: 0,
                            status: 'active',
                          })
                        }
                        block
                        icon={<Icon.Plus size={16} />}
                        style={{ marginBottom: '16px' }}
                      >
                        Add Benefit
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form>
            ) : (
              quotationData?.additional_benefits.length  > 0 ? (
                <TableComponent
                  table_fields={benefitsTableFields}
                  table_data={quotationData?.additional_benefits || []}
                  row_data={benefitsTableRowData}
                  pageSize={10}
                  hasAction={isEditingBenefits}
                  table_class="table table-hover"
                />
              ) : (
                <div className="text-center py-5">
                  <Icon.CheckCircle size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">No benefits included</h6>
                  <Text type="secondary">This quotation has no specific benefits configured.</Text>
                </div>
              )
            )}
          </Card>
        </TabPane>

        <TabPane tab={<Space><Icon.User size={16} /> Client Details</Space>} key="3">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '16px' }}>
            {isEditingClient ? (
              <Form
                form={clientForm}
                onFinish={handleClientUpdate}
                layout="vertical"
                style={{ maxWidth: '600px', margin: '0 auto' }}
              >
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon.User size={18} style={{ color: '#1890ff' }} />
                  <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Edit Client Information</Title>
                </div>
                <Form.Item
                  name="kycType"
                  label="Client Type"
                  rules={[{ required: true, message: 'Please select client type' }]}
                >
                  <Select>
                    <Option value="INDIVIDUAL">Individual</Option>
                    <Option value="CORPORATE">Corporate</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: 'Please input first name' }]}
                >
                  <Input size="large" placeholder="Enter first name" />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please input last name' }]}
                >
                  <Input size="large" placeholder="Enter last name" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please input email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input size="large" placeholder="Enter email" />
                </Form.Item>
                <Form.Item
                  name="kraPin"
                  label="KRA PIN"
                  rules={[{ required: true, message: 'Please input KRA PIN' }]}
                >
                  <Input size="large" placeholder="Enter KRA PIN" />
                </Form.Item>
                <Space style={{ marginTop: '16px' }}>
                  <Button size="large" onClick={() => setIsEditingClient(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" size="large" htmlType="submit">
                    Save Changes
                  </Button>
                </Space>
              </Form>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon.User size={20} style={{ color: '#1890ff' }} />
                    <Title level={4} style={{ margin: 0, color: '#1f2937' }}>Client Information</Title>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<Icon.Edit size={16} />}
                    onClick={() => setIsEditingClient(true)}
                  >
                    Edit Client Details
                  </Button>
                </div>
                <div style={{ maxWidth: '600px', margin: '0 auto', background: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                    <Avatar
                      size={64}
                      src={`https://ui-avatars.com/api/?name=${quotationData?.clientDetails?.names?.fname || ''}+${quotationData?.clientDetails?.names?.lname || ''}&background=1890ff&color=fff&size=128&bold=true`}
                      style={{ background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)', color: '#fff' }}
                    >
                      {quotationData?.clientDetails?.names?.firstName?.charAt(0) || ''}{quotationData?.clientDetails?.names?.lname?.charAt(0) || ''}
                    </Avatar>
                    <div>
                      <Text strong style={{ fontSize: '18px', color: '#1f2937' }}>
                        {quotationData?.clientDetails?.names?.fname || ''} {quotationData?.clientDetails?.names?.lname || ''}
                      </Text>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {quotationData?.clientDetails?.kyc?.[0] === "I" ? "INDIVIDUAL" : "CORPORATE"}
                      </div>
                    </div>
                  </div>
                  <Descriptions
                    bordered
                    column={1}
                    size="middle"
                    labelStyle={{ width: '30%', fontWeight: 500, background: '#fafafa' }}
                    contentStyle={{ background: '#fff' }}
                    style={{ borderRadius: '8px' }}
                  >
                    <Descriptions.Item label="First Name">
                      <span>{quotationData?.clientDetails?.names?.firstName || 'N/A'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Name">
                      <span>{quotationData?.clientDetails?.names?.lastName || 'N/A'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon.Mail size={16} style={{ color: '#1890ff' }} />
                        <a href={`mailto:${quotationData?.clientDetails?.email || ''}`} style={{ color: '#1890ff' }}>
                          {quotationData?.clientDetails?.email || 'N/A'}
                        </a>
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="KRA PIN">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon.Hash size={16} style={{ color: '#1890ff' }} />
                        <span>{quotationData?.clientDetails?.kraPin || 'N/A'}</span>
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </div>
            )}
          </Card>
        </TabPane>

        <TabPane tab={<Space><Icon.AlertTriangle size={16} /> Risk Details</Space>} key="4">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            {isEditingRisk ? (
              <Form
                form={riskForm}
                onFinish={handleRiskUpdate}
                layout="vertical"
                style={{ maxWidth: '600px', margin: '0 auto' }}
              >
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon.Truck size={18} style={{ color: '#1890ff' }} />
                  <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Edit Vehicle Risk Information</Title>
                </div>
                <Form.Item
                  name="value"
                  label="Insured Value"
                  rules={[{ required: true, message: 'Please input insured value' }]}
                >
                  <Input type="number" prefix={quotationData?.quotationCurrency || ''} min={0} />
                </Form.Item>
                <Form.Item
                  name="make"
                  label="Make"
                  rules={[{ required: true, message: 'Please input vehicle make' }]}
                >
                  <Input placeholder="Enter vehicle make" />
                </Form.Item>
                <Form.Item
                  name="model"
                  label="Model"
                  rules={[{ required: true, message: 'Please input vehicle model' }]}
                >
                  <Input placeholder="Enter vehicle model" />
                </Form.Item>
                <Form.Item
                  name="year"
                  label="Year"
                  rules={[{ required: true, message: 'Please input vehicle year' }]}
                >
                  <Input type="number" min={1900} max={new Date().getFullYear() + 1} placeholder="Enter year" />
                </Form.Item>
                <Form.Item
                  name="color"
                  label="Color"
                  rules={[{ required: true, message: 'Please input vehicle color' }]}
                >
                  <Input placeholder="Enter color" />
                </Form.Item>
                <Form.Item
                  name="chassis"
                  label="Chassis Number"
                  rules={[{ required: true, message: 'Please input chassis number' }]}
                >
                  <Input placeholder="Enter chassis number" />
                </Form.Item>
                <Space style={{ marginTop: '16px' }}>
                  <Button onClick={() => setIsEditingRisk(false)}>Cancel</Button>
                  <Button type="primary" htmlType="submit">Save</Button>
                </Space>
              </Form>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon.Truck size={18} style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>Vehicle Risk Information</span>
                  </div>
                  <Button
                    type="primary"
                    icon={<Icon.Edit size={16} />}
                    onClick={() => setIsEditingRisk(true)}
                  >
                    Edit Risk Details
                  </Button>
                </div>
                <Descriptions
                  bordered
                  column={2}
                  size="small"
                  style={{ background: '#fff', borderRadius: '8px' }}
                >
                  <Descriptions.Item label="Insured Value">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>{quotationData?.quotationCurrency || ''} {quotationData?.risks?.[0]?.value?.toLocaleString() || '0'}</span>
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Make">
                    <Badge color="geekblue" text={quotationData?.risks?.[0]?.details?.make || 'N/A'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Model">
                    <Badge color="cyan" text={quotationData?.risks?.[0]?.details?.model || 'N/A'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Year">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon.Calendar size={14} />
                      <span>{quotationData?.risks?.[0]?.details?.year || 'N/A'}</span>
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Color">
                    <Badge color="gold" text={quotationData?.risks?.[0]?.details?.color || 'N/A'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Chassis Number">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon.Key size={14} />
                      <span>{quotationData?.risks?.[0]?.details?.chassis || 'N/A'}</span>
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </Card>
        </TabPane>

        <TabPane tab={<Space><Icon.DollarSign size={16} /> Commission</Space>} key="5">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon.DollarSign size={18} style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>Commission</span>
                  </div>  
                </div>
                <Descriptions
                  bordered
                  column={2}
                  size="small"
                  style={{ background: '#fff', borderRadius: '8px' }}
                >
                  <Descriptions.Item label='Commission Amount'>
                    {quotationData.commission.commisionAmount}
                  </Descriptions.Item>
                   <Descriptions.Item label='Commission Value'>
                    {quotationData.commission.appliedValue}
                  </Descriptions.Item>
                 
                </Descriptions>
              </div>
      
          </Card>
        </TabPane>


        <TabPane tab={<Space><Icon.Hash size={16} /> Premium Calculation Logic</Space>} key="6">
          <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>         
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon.Hash size={18} style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>Logic</span>
                  </div>
                </div>
                <Descriptions
                  bordered
                  column={2}
                  size="small"
                  style={{ background: '#fff', borderRadius: '8px' }}
                >
                  {quotationData.quotationPremiumCalculationLogic.length > 0 && (
                     quotationData.quotationPremiumCalculationLogic.map((quote) => (<Descriptions.Item label={quote}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span></span>
                    </div>
                  </Descriptions.Item>))
                  )}
                  
                </Descriptions>
              </div>
          </Card>
        </TabPane>
        
        
      </Tabs>
    </div>
  );
}